import { Button, Select, Space } from "antd";
import dayjsTZ from "../../../utils/dayjs";

const MONTHS = [
  "Janar",
  "Shkurt",
  "Mars",
  "Prill",
  "Maj",
  "Qershor",
  "Korrik",
  "Gusht",
  "Shtator",
  "Tetor",
  "Nëntor",
  "Dhjetor",
];

const currentYear = dayjsTZ().year();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - i);

/**
 * Year / Quarter / Month / Departament / Team / Punonjës filters.
 * Changes stay local until Apliko is pressed.
 */
export default function AnalyticsFilterBar({
  value,
  onChange,
  onApply,
  applyDisabled = false,
  loading = false,
  filterOptions,
  isDarkMode = false,
}) {
  const set = (patch) => onChange({ ...value, ...patch });
  const activeOnly = (value.employeeScope || "active") !== "all";

  const scopedEmployees = (filterOptions?.employees || []).filter((e) => {
    if (activeOnly && !e.active) return false;
    if (value.department && e.department !== value.department) return false;
    if (value.team && e.team !== value.team) return false;
    return true;
  });

  const departmentOptions = [
    ...new Set(
      (filterOptions?.employees || [])
        .filter((e) => !activeOnly || e.active)
        .map((e) => e.department)
        .filter(Boolean),
    ),
  ].sort();

  const teamOptions = [
    ...new Set(
      (filterOptions?.employees || [])
        .filter((e) => {
          if (activeOnly && !e.active) return false;
          if (value.department && e.department !== value.department) {
            return false;
          }
          return !!e.team;
        })
        .map((e) => e.team),
    ),
  ].sort();

  const popupClass = isDarkMode ? "darkDropdown" : "";

  return (
    <Space wrap size={[8, 8]} className="analytics-filter-bar">
      <Select
        style={{ minWidth: 150 }}
        value={value.employeeScope || "active"}
        onChange={(employeeScope) =>
          set({
            employeeScope,
            // Drop selected employee if they fall outside the new scope
            employeeId:
              employeeScope === "active" &&
              value.employeeId &&
              !(filterOptions?.employees || []).find(
                (e) => e.value === value.employeeId && e.active,
              )
                ? null
                : value.employeeId,
          })
        }
        options={[
          { value: "active", label: "Vetëm aktivë" },
          { value: "all", label: "Të gjithë" },
        ]}
        aria-label="Statusi i punonjësve"
        classNames={{ popup: { root: popupClass } }}
      />
      <Select
        style={{ minWidth: 100 }}
        value={value.year}
        onChange={(year) => set({ year })}
        options={YEARS.map((y) => ({ value: y, label: String(y) }))}
        aria-label="Viti"
        classNames={{ popup: { root: popupClass } }}
      />
      <Select
        allowClear
        placeholder="Tremujori"
        style={{ minWidth: 120 }}
        value={value.quarter ?? undefined}
        onChange={(quarter) =>
          set({
            quarter: quarter ?? null,
            month: quarter != null ? null : value.month,
          })
        }
        options={[1, 2, 3, 4].map((q) => ({
          value: q,
          label: `T${q}`,
        }))}
        classNames={{ popup: { root: popupClass } }}
      />
      <Select
        allowClear
        placeholder="Muaji"
        style={{ minWidth: 130 }}
        value={value.month ?? undefined}
        onChange={(month) =>
          set({
            month: month ?? null,
            quarter: month != null ? null : value.quarter,
          })
        }
        options={MONTHS.map((label, idx) => ({ value: idx, label }))}
        classNames={{ popup: { root: popupClass } }}
      />
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="Departamenti"
        style={{ minWidth: 170 }}
        value={value.department || undefined}
        onChange={(department) =>
          set({ department: department || null, employeeId: null })
        }
        options={departmentOptions.map((d) => ({
          value: d,
          label: d,
        }))}
        classNames={{ popup: { root: popupClass } }}
      />
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="Ekipi"
        style={{ minWidth: 150 }}
        value={value.team || undefined}
        onChange={(team) => set({ team: team || null, employeeId: null })}
        options={teamOptions.map((t) => ({ value: t, label: t }))}
        classNames={{ popup: { root: popupClass } }}
      />
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="Punonjësi"
        style={{ minWidth: 180 }}
        value={value.employeeId || undefined}
        onChange={(employeeId) => set({ employeeId: employeeId || null })}
        options={scopedEmployees.map((e) => ({
          value: e.value,
          label: e.label,
        }))}
        classNames={{ popup: { root: popupClass } }}
      />
      <Button
        type="primary"
        className="analytics-filter-apply"
        onClick={onApply}
        disabled={applyDisabled}
        loading={loading}
      >
        Apliko
      </Button>
    </Space>
  );
}
